import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Section, Eyebrow, H2, GlassCard } from "../components/site/Primitives";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { ShieldCheck, Briefcase, Home as HomeIcon, KeyRound, HardHat, ArrowRight } from "lucide-react";

const ROLE_INFO = {
  admin: { icon: ShieldCheck, title: "Admin Dashboard", href: "/portal/admin" },
  investor: { icon: Briefcase, title: "Investor Portal", href: "/portal/investor" },
  resident: { icon: HomeIcon, title: "Resident Portal", href: "/portal/resident" },
  tenant: { icon: KeyRound, title: "Tenant Portal", href: "/portal/tenant" },
  contractor: { icon: HardHat, title: "Contractor Portal", href: "/portal/contractor" },
  property_manager: { icon: ShieldCheck, title: "Property Manager", href: "/portal/admin" },
  employee: { icon: Briefcase, title: "Employee Portal", href: "/portal/admin" },
  guest: { icon: Briefcase, title: "Explore Portals", href: "#" },
};

export default function PortalHub() {
  const { user, loading } = useAuth();
  const [updating, setUpdating] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/60">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const switchRole = async (role) => {
    setUpdating(true);
    try { await api.post("/auth/role", { role }); window.location.reload(); }
    finally { setUpdating(false); }
  };

  const myInfo = ROLE_INFO[user.role] || ROLE_INFO.guest;
  const Icon = myInfo.icon;

  return (
    <Section className="pt-40">
      <Eyebrow>Welcome, {user.name}</Eyebrow>
      <H2>Your 220 Inc. Portal</H2>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <GlassCard className="p-8 lg:col-span-1">
          <div className="text-gold mb-4"><Icon size={28} /></div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/40">Current Role</div>
          <div className="font-display text-2xl mt-1 mb-4 capitalize">{user.role.replace(/_/g, " ")}</div>
          <p className="text-sm text-white/65 mb-6">Email: {user.email}</p>
          {myInfo.href !== "#" && (
            <Link to={myInfo.href} className="btn-primary text-sm" data-testid="portal-enter">
              Enter {myInfo.title} <ArrowRight size={14} />
            </Link>
          )}
        </GlassCard>

        <GlassCard className="p-8 lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">Switch Role (Demo)</div>
          <div className="font-display text-2xl mb-4">Try a different portal experience.</div>
          <p className="text-sm text-white/60 mb-6">V1 demo: change your role to preview different dashboards. In production, role changes go through admin approval.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.keys(ROLE_INFO).filter(r => r !== "guest").map((r) => (
              <button key={r} onClick={() => switchRole(r)} disabled={updating}
                className={`border px-4 py-3 text-sm transition-colors text-left ${user.role === r ? "border-gold text-gold" : "border-white/10 text-white/80 hover:border-white/30"}`}
                data-testid={`role-${r}`}>
                <div className="text-xs uppercase tracking-[0.18em] text-white/40">Role</div>
                <div className="capitalize">{r.replace(/_/g, " ")}</div>
              </button>
            ))}
          </div>
        </GlassCard>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: "Investor Portal", href: "/portal/investor" },
          { label: "Resident Portal", href: "/portal/resident" },
          { label: "Tenant Portal", href: "/portal/tenant" },
          { label: "Contractor Portal", href: "/portal/contractor" },
          { label: "Admin Dashboard", href: "/portal/admin" },
        ].map((p) => (
          <Link key={p.href} to={p.href} className="border border-white/10 p-5 hover:border-gold transition-colors flex items-center justify-between">
            <span className="text-sm">{p.label}</span><ArrowRight size={14} className="text-gold" />
          </Link>
        ))}
      </div>
    </Section>
  );
}
